import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Sector,
} from 'recharts';
import ReportsApi from '../ReportsContainer/reports-api';

const COLORS = {
  Completo: '#E07B2F',
  'Paso 2': '#F5B942',
  'Paso 1': '#F9E08A',
};

const PIE_COLORS = ['#E07B2F', '#F5B942', '#F9E08A', '#6C9EC4', '#4C7A6B', '#C46C6C'];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2, CURRENT_YEAR - 3, CURRENT_YEAR - 4];

const ROLE_OPTIONS = [
  { id: 1, label: 'Mentee' },
  { id: 2, label: 'Mentor' },
  { id: 4, label: 'Ninguno' },
];

// ── KPI Card ───────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, accent }) => (
  <div style={{
    background: '#fff',
    borderRadius: 12,
    padding: '20px 24px',
    flex: 1,
    minWidth: 140,
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    borderTop: `4px solid ${accent || '#E07B2F'}`,
  }}>
    <div style={{ fontSize: 13, color: '#888', marginBottom: 6, fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 32, fontWeight: 700, color: '#222' }}>
      {value !== null && value !== undefined ? value.toLocaleString('es-CL') : '—'}
    </div>
  </div>
);

// ── Custom Tooltip ─────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, normalized }) => {
  if (!active || !payload || !payload.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e8e8e8',
      borderRadius: 8,
      padding: '10px 14px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      fontSize: 13,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6, color: '#333' }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: p.fill || p.color }} />
          <span style={{ color: '#555' }}>{p.name}:</span>
          <span style={{ fontWeight: 600, color: '#222' }}>
            {normalized
              ? `${total > 0 ? (((p.value || 0) / total) * 100).toFixed(1) : '0.0'}%`
              : (p.value || 0).toLocaleString('es-CL')}
          </span>
        </div>
      ))}
      {!normalized && payload.length > 1 && (
        <div style={{ borderTop: '1px solid #eee', marginTop: 6, paddingTop: 6, color: '#888', fontSize: 12 }}>
          Total: <strong style={{ color: '#222' }}>{total.toLocaleString('es-CL')}</strong>
        </div>
      )}
    </div>
  );
};

// ── Active Pie Shape ───────────────────────────────────────────────────────
const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  return (
    <g>
      <text x={cx} y={cy - 10} textAnchor="middle" fill="#222" style={{ fontSize: 22, fontWeight: 700 }}>
        {value.toLocaleString('es-CL')}
      </text>
      <text x={cx} y={cy + 16} textAnchor="middle" fill="#888" style={{ fontSize: 12 }}>
        {payload.name}
      </text>
      <text x={cx} y={cy + 34} textAnchor="middle" fill="#aaa" style={{ fontSize: 11 }}>
        {(percent * 100).toFixed(1)}%
      </text>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 6}
        startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} innerRadius={outerRadius + 10} outerRadius={outerRadius + 14}
        startAngle={startAngle} endAngle={endAngle} fill={fill} />
    </g>
  );
};

// ── Section Title ──────────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
  <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: '#444', letterSpacing: 0.2 }}>
    {children}
  </h3>
);

const ChartCard = ({ children, style }) => (
  <div style={{
    background: '#fff',
    borderRadius: 12,
    padding: '20px 20px 12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    ...style,
  }}>
    {children}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────
const MenteesDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [selectedRoles, setSelectedRoles] = useState([1]);
  const [activeStep, setActiveStep] = useState(0);
  const [activeGender, setActiveGender] = useState(0);
  const [viewMode, setViewMode] = useState('mensual'); // 'semanal' | 'mensual'
  const [barMode, setBarMode] = useState('absoluto'); // 'absoluto' | 'porcentual'

  const toggleRole = (id) => {
    setSelectedRoles((prev) => {
      if (prev.includes(id)) {
        if (prev.length === 1) return prev;
        return prev.filter((r) => r !== id);
      }
      return [...prev, id];
    });
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    ReportsApi.getMenteesDashboard(selectedYear, selectedRoles)
      .then((res) => setData(res.data))
      .catch(() => setError('No se pudo cargar el dashboard. Intenta nuevamente.'))
      .finally(() => setLoading(false));
  }, [selectedYear, selectedRoles]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: '#888' }}>
      Cargando estadísticas...
    </div>
  );

  if (error) return (
    <div style={{ color: '#c0392b', padding: 24, background: '#fff5f5', borderRadius: 8, margin: 16 }}>
      {error}
    </div>
  );

  if (!data) return null;

  const chartData = viewMode === 'semanal' ? data.semanal : data.mensual;
  const xKey = viewMode === 'semanal' ? 'semana' : 'mes';

  // Porcentaje de completitud
  const pctCompleto = data.kpis.total > 0
    ? ((data.kpis.completo / data.kpis.total) * 100).toFixed(1)
    : 0;

  const stepPieData = [
    { name: 'Completo', value: data.kpis.completo },
    { name: 'Paso 2', value: data.kpis.paso2 },
    { name: 'Paso 1', value: data.kpis.paso1 },
  ].filter((d) => d.value > 0);

  return (
    <div style={{ padding: '24px', background: '#f4f6f9', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>
              Registro {ROLE_OPTIONS.filter((r) => selectedRoles.includes(r.id)).map((r) => r.label).join(' + ')} {selectedYear}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>
              Estadísticas de registro y completitud
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {YEARS.map((y) => (
              <button key={y} onClick={() => setSelectedYear(y)} style={{
                padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: selectedYear === y ? '#E07B2F' : '#fff',
                color: selectedYear === y ? '#fff' : '#555',
                boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                transition: 'all 0.2s',
              }}>
                {y}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#888', fontWeight: 500, marginRight: 4 }}>Rol:</span>
          {ROLE_OPTIONS.map((role) => {
            const active = selectedRoles.includes(role.id);
            return (
              <button key={role.id} onClick={() => toggleRole(role.id)} style={{
                padding: '5px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                border: `2px solid ${active ? '#4C7A6B' : '#ddd'}`,
                background: active ? '#4C7A6B' : '#fff',
                color: active ? '#fff' : '#888',
                boxShadow: active ? '0 1px 4px rgba(76,122,107,0.25)' : 'none',
                transition: 'all 0.2s',
              }}>
                {role.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <KpiCard label="Total histórico" value={data.totalHistorico} accent="#1a1a2e" />
        <KpiCard label="Registrados" value={data.kpis.total} accent="#E07B2F" />
        <KpiCard label="Completo" value={data.kpis.completo} accent="#E07B2F" />
        <KpiCard label="Paso 1" value={data.kpis.paso1} accent="#F9E08A" />
        <KpiCard label="Paso 2" value={data.kpis.paso2} accent="#F5B942" />
        <KpiCard label="% Completitud" value={`${pctCompleto}%`} accent="#4C9A78" />
      </div>

      {/* Stacked Bar Chart + View Toggle */}
      <ChartCard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <SectionTitle>Registros por {viewMode === 'semanal' ? 'semana' : 'mes'}</SectionTitle>
          <div style={{ display: 'flex', gap: 6 }}>
            {['semanal', 'mensual'].map((mode) => (
              <button key={mode} onClick={() => setViewMode(mode)} style={{
                padding: '4px 12px', borderRadius: 16, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: viewMode === mode ? '#E07B2F' : '#f0f0f0',
                color: viewMode === mode ? '#fff' : '#555',
                transition: 'all 0.2s',
              }}>
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
            <div style={{ width: 1, background: '#e0e0e0', margin: '0 4px' }} />
            {['absoluto', 'porcentual'].map((mode) => (
              <button key={mode} onClick={() => setBarMode(mode)} style={{
                padding: '4px 12px', borderRadius: 16, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: barMode === mode ? '#4C7A6B' : '#f0f0f0',
                color: barMode === mode ? '#fff' : '#555',
                transition: 'all 0.2s',
              }}>
                {mode === 'absoluto' ? '#' : '%'}
              </button>
            ))}
          </div>
        </div>
        {chartData && chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={chartData}
              margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
              barCategoryGap="25%"
              stackOffset={barMode === 'porcentual' ? 'expand' : 'none'}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 11, fill: '#888' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={barMode === 'porcentual' ? (v) => `${(v * 100).toFixed(0)}%` : undefined}
              />
              <Tooltip content={<CustomTooltip normalized={barMode === 'porcentual'} />} />
              <Legend wrapperStyle={{ fontSize: 13, paddingTop: 12 }} />
              <Bar dataKey="Completo" stackId="a" fill={COLORS.Completo} radius={[0, 0, 0, 0]} />
              <Bar dataKey="Paso 2" stackId="a" fill={COLORS['Paso 2']} />
              <Bar dataKey="Paso 1" stackId="a" fill={COLORS['Paso 1']} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>Sin datos para este período</div>
        )}
      </ChartCard>

      {/* Row: Line Chart + Donut */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 24, flexWrap: 'wrap' }}>

        {/* Line Chart - tendencia */}
        <ChartCard style={{ flex: 2, minWidth: 300 }}>
          <SectionTitle>Tendencia por estado de registro</SectionTitle>
          {chartData && chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 13, paddingTop: 12 }} />
                <Line type="monotone" dataKey="Completo" stroke={COLORS.Completo} strokeWidth={2.5}
                  dot={{ r: 4, fill: COLORS.Completo }} activeDot={{ r: 6 }} />
                <Line type="monotone" dataKey="Paso 2" stroke={COLORS['Paso 2']} strokeWidth={2.5}
                  dot={{ r: 4, fill: COLORS['Paso 2'] }} activeDot={{ r: 6 }} />
                <Line type="monotone" dataKey="Paso 1" stroke="#D4B800" strokeWidth={2.5}
                  dot={{ r: 4, fill: '#D4B800' }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>Sin datos</div>
          )}
        </ChartCard>

        {/* Donut - distribución por paso */}
        <ChartCard style={{ flex: 1, minWidth: 240 }}>
          <SectionTitle>Distribución por paso</SectionTitle>
          {stepPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  activeIndex={activeStep}
                  activeShape={renderActiveShape}
                  data={stepPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={68}
                  outerRadius={95}
                  dataKey="value"
                  onMouseEnter={(_, idx) => setActiveStep(idx)}
                >
                  {stepPieData.map((entry, idx) => (
                    <Cell key={entry.name} fill={Object.values(COLORS)[idx] || PIE_COLORS[idx]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => v.toLocaleString('es-CL')} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>Sin datos</div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {stepPieData.map((d, idx) => (
              <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: Object.values(COLORS)[idx] || PIE_COLORS[idx] }} />
                <span style={{ color: '#666' }}>{d.name}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Row: Region Bar + Gender Donut */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>

        {/* Horizontal Bar - por región */}
        {data.region && data.region.length > 0 && (
          <ChartCard style={{ flex: 2, minWidth: 300 }}>
            <SectionTitle>Top regiones</SectionTitle>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={data.region}
                layout="vertical"
                margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="region" tick={{ fontSize: 11, fill: '#555' }} width={130} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="cantidad" fill="#E07B2F" radius={[0, 4, 4, 0]} name="Mentees">
                  {data.region.map((_, idx) => (
                    <Cell key={idx} fill={`hsl(${25 + idx * 8}, ${80 - idx * 4}%, ${55 + idx * 3}%)`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Donut - por género */}
        {data.genero && data.genero.length > 0 && (
          <ChartCard style={{ flex: 1, minWidth: 240 }}>
            <SectionTitle>Distribución por género</SectionTitle>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  activeIndex={activeGender}
                  activeShape={renderActiveShape}
                  data={data.genero}
                  cx="50%"
                  cy="50%"
                  innerRadius={68}
                  outerRadius={95}
                  dataKey="value"
                  onMouseEnter={(_, idx) => setActiveGender(idx)}
                >
                  {data.genero.map((_, idx) => (
                    <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => v.toLocaleString('es-CL')} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
              {data.genero.map((d, idx) => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: PIE_COLORS[idx % PIE_COLORS.length] }} />
                  <span style={{ color: '#666' }}>{d.name}</span>
                </div>
              ))}
            </div>
          </ChartCard>
        )}
      </div>
    </div>
  );
};

export default MenteesDashboard;
